import { GridFilterItem, GridFilterOperator } from '@mui/x-data-grid-pro';
import { RevisionRoles } from '../__generated__/graphql';
import { progressResponsibilityRoles, revisionRoleToDisplayText } from './RevisionModel';
import { newestAssessment, Revision } from './types';

export const staleDays = 90;

export const staleAssessmentDays = 60;

const daysAgo = (days: number) => new Date(Date.now() - days * 24 * 60 * 60 * 1000);

const isStaleDate = (value: Date | null | undefined, days: number) => !value || value < daysAgo(days);

const isOverdue = (value: Date | null | undefined) => !!value && value < new Date();

const toDate = (value: string | null | undefined) => (value ? new Date(value) : null);

const newestAssessmentDate = (revision: Revision) => toDate(newestAssessment(revision)?.createdAt);

const lastMovedAt = (revision: Pick<Revision, 'lastActivityAt' | 'createdAt'>) => toDate(revision.lastActivityAt) ?? toDate(revision.createdAt);

export const isOngoing = (revision: Revision) => !revision.completedAt;

export const ongoingFilterItem: Omit<GridFilterItem, 'id'> = { field: 'completedAt', operator: 'isEmpty' };

export type RevisionKpi = {
  key: string;
  label: string;
  help: string;
  severity?: 'warning' | 'error';
  matches: (revision: Revision) => boolean;
  filterItem: Omit<GridFilterItem, 'id'>;
};

export const revisionKpis: RevisionKpi[] = [
  {
    key: 'stale',
    label: `Stille i ${staleDays} dager`,
    help: `Ingen aktivitet påbegynt eller avhuket, ingen sjekklistepunkter krysset av og ingen endring av «Ligger hos» de siste ${staleDays} dagene. En revisjon uten noe av dette regnes fra den ble opprettet.`,
    severity: 'warning',
    matches: (revision) => isOngoing(revision) && isStaleDate(lastMovedAt(revision), staleDays),
    filterItem: { field: 'lastActivityAt', operator: 'isStale' },
  },
  {
    key: 'noResponsibility',
    label: 'Ligger hos ingen',
    help: '«Ligger hos» er ikke satt, så ingen rolle har ansvaret for fremdriften.',
    matches: (revision) => isOngoing(revision) && (revision.progressResponsibility ?? []).length === 0,
    filterItem: { field: 'progressResponsibility', operator: 'isEmpty' },
  },
  {
    key: 'staleAssessment',
    label: 'Uten fersk oppdatering',
    help: `Prosjektoppdatering mangler, eller den nyeste er eldre enn ${staleAssessmentDays} dager.`,
    matches: (revision) => isOngoing(revision) && isStaleDate(newestAssessmentDate(revision), staleAssessmentDays),
    filterItem: { field: 'projectLeadAssessments', operator: 'isStaleAssessment' },
  },
];

export const staleOperator: GridFilterOperator<Revision, Date | null> = {
  label: `har ligget stille i ${staleDays} dager`,
  value: 'isStale',
  requiresFilterValue: false,
  getApplyFilterFn: () => (value, row) => isStaleDate(value ?? toDate(row.createdAt), staleDays),
};

export const overdueOperator: GridFilterOperator<any, Date | null> = {
  label: 'er passert',
  value: 'isOverdue',
  requiresFilterValue: false,
  getApplyFilterFn: () => (value) => isOverdue(value),
};

export const staleAssessmentOperator: GridFilterOperator<any, Date | null> = {
  label: `mangler eller er eldre enn ${staleAssessmentDays} dager`,
  value: 'isStaleAssessment',
  requiresFilterValue: false,
  getApplyFilterFn: () => (value) => isStaleDate(value, staleAssessmentDays),
};

export function countByProgressResponsibility(revisions: Revision[]) {
  return progressResponsibilityRoles.map((role) => ({
    role,
    label: revisionRoleToDisplayText(role),
    count: revisions.filter((revision) => isOngoing(revision) && revision.progressResponsibility?.includes(role)).length,
  }));
}

export type ProgressResponsibilityCount = { role: RevisionRoles; label: string; count: number };
